import { useCallback, useEffect, useState } from 'react';
import { useTRPCClientAPI } from '../../../../trpc/trpc';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';
import { cardPhaseOfAnswer, cardPhaseOfStatus, SongsBrewCardPhase } from './songsBrewCardPhase';
import { SEND_TRACK_FALLBACK_URL, useSongsBrewState } from './useSongsBrewState';
import { useWriteSongsBrewStatus } from './useWriteSongsBrewStatus';

export interface SongsBrewHomeCard {
  /** Null: the card does not show (loading, an error, an old server, not visible, closed, demo). */
  phase: SongsBrewCardPhase | null;
  sendTrackUrl: string;
  subscribe: () => void;
  dismiss: () => void;
}

/**
 * The Home card rules (design: songsbrew-promotion a16). The card shows when `newsletter.getState`
 * answers `available` and `cardVisible`, and not for the demo artist. Home reads the platform again
 * on each mount, and the first phase comes only from that read, never from a cached answer: a
 * subscribe in Settings or on the welcome does not show the offer again. The first phase is kept for
 * the page view: the done card stays until the next Home load, also when a refetch says
 * `cardVisible: false`. A close is optimistic and calls `newsletter.dismissCard`.
 */
export function useSongsBrewHomeCard(): SongsBrewHomeCard {
  const trpcClient = useTRPCClientAPI();
  const writeStatus = useWriteSongsBrewStatus();
  const isDemo = useIsDemoAccount();
  const { state, isFetchedAfterMount } = useSongsBrewState({ enabled: !isDemo, refetchOnMount: 'always' });
  const [phase, setPhase] = useState<SongsBrewCardPhase | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (phase !== null || !isFetchedAfterMount || state === null || !state.cardVisible) {
      return;
    }
    setPhase(cardPhaseOfStatus(state.status));
  }, [isFetchedAfterMount, phase, state]);

  const subscribe = useCallback(() => {
    if (phase === 'busy') {
      return;
    }
    setPhase('busy');
    trpcClient.newsletter.subscribe
      .mutate({ source: 'music24-home' })
      .then((answer) => {
        setPhase(cardPhaseOfAnswer(answer.status));
        // Settings and the welcome read the new status.
        writeStatus(answer.status);
      })
      .catch((error: unknown) => {
        console.error(error);
        setPhase('error');
      });
  }, [phase, trpcClient, writeStatus]);

  const dismiss = useCallback(() => {
    // A failed call still hides the card for this page view (design: songsbrew-promotion a16).
    setIsDismissed(true);
    trpcClient.newsletter.dismissCard.mutate().catch(console.error);
  }, [trpcClient]);

  return {
    phase: isDemo || isDismissed ? null : phase,
    sendTrackUrl: state?.sendTrackUrl ?? SEND_TRACK_FALLBACK_URL,
    subscribe,
    dismiss,
  };
}
